/**
 * Workbook Optimization Panel Component
 * Affiche les suggestions d'optimisation et les plans d'amélioration
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { WorkbookOptimizationPlan, OptimizationPriority } from '@excel-ai-studio/shared';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';

interface WorkbookOptimizationPanelProps {
  onImplement?: (priority: OptimizationPriority) => void;
}

export const WorkbookOptimizationPanel: React.FC<WorkbookOptimizationPanelProps> = ({
  onImplement,
}) => {
  const [plan, setPlan] = useState<WorkbookOptimizationPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'priorities' | 'quickwins' | 'structural' | 'performance' | 'maintenance'>(
    'priorities'
  );
  const [expandedPriority, setExpandedPriority] = useState<number | null>(null);

  const generatePlan = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await callAnalyzeAPI({
        includeScanning: true,
        includeRiskAnalysis: true,
        includePerformanceAnalysis: true,
        includeOptimization: true,
      });

      if (!result.optimizationPlan) {
        throw new Error('Aucun plan d\'optimisation retourné par le serveur');
      }

      setPlan(result.optimizationPlan);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de générer le plan d\'optimisation');
    } finally {
      setIsLoading(false);
    }
  };

  const getEffortColor = (effort: string): string => {
    switch (effort) {
      case 'low': return '#4CAF50';
      case 'medium': return '#FF9800';
      case 'high': return '#F44336';
      default: return '#999';
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'high': return '#d32f2f';
      case 'medium': return '#f57c00';
      case 'low': return '#388e3c';
      default: return '#1976d2';
    }
  };

  const getImpactLabel = (impact: string): string => {
    switch (impact) {
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return impact;
    }
  };

  const getEffortLabel = (effort: string): string => {
    switch (effort) {
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return effort;
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Génération du plan d'optimisation..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>🚀 Optimisation</h3>
        <button
          onClick={generatePlan}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Générer le plan
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          ❌ {error}
        </div>
      )}

      {plan && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Bénéfice estimé */}
          <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #4CAF50', fontSize: '13px' }}>
            <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '4px' }}>
              📈 Bénéfice total estimé
            </div>
            <div style={{ color: '#1b5e20' }}>{plan.estimatedTotalBenefit}</div>
          </div>

          {/* Navigation par onglets */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
            {[
              { id: 'priorities', label: '🎯 Priorités' },
              { id: 'quickwins', label: '💡 Gains rapides' },
              { id: 'structural', label: '🏗️ Structurel' },
              { id: 'performance', label: '⚡ Performance' },
              { id: 'maintenance', label: '🔧 Maintenance' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: activeTab === tab.id ? '#2196F3' : '#f0f0f0',
                  color: activeTab === tab.id ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Onglet Priorités */}
          {activeTab === 'priorities' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {plan.priorities.map((priority, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    backgroundColor: 'white',
                    border: `2px solid ${getImpactColor(priority.impact)}`,
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                  }}
                >
                  <div 
                    onClick={() => setExpandedPriority(expandedPriority === idx ? null : idx)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>
                        {priority.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        {priority.description}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                        <span style={{ backgroundColor: getImpactColor(priority.impact), color: 'white', padding: '2px 6px', borderRadius: '3px' }}>
                          Impact : {getImpactLabel(priority.impact)}
                        </span>
                        <span style={{ backgroundColor: getEffortColor(priority.effort), color: 'white', padding: '2px 6px', borderRadius: '3px' }}>
                          Effort : {getEffortLabel(priority.effort)}
                        </span>
                      </div>
                    </div>
                    <span style={{ fontSize: '18px', marginLeft: '8px' }}>
                      {expandedPriority === idx ? '▼' : '▶'}
                    </span>
                  </div>

                  {expandedPriority === idx && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                          ACTIONS SUGGÉRÉES
                        </div>
                        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#333' }}>
                          {priority.suggestedActions.map((action, i) => (
                            <li key={i}>{action}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                          BÉNÉFICE ESTIMÉ
                        </div>
                        <div style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 'bold' }}>
                          {priority.estimatedBenefit}
                        </div>
                      </div>
                      {priority.affectedAreas && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                            ZONES AFFECTÉES
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {priority.affectedAreas.join(', ')}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          console.log('🔘 CLICK EVENT FIRED');
                          e.stopPropagation();
                          console.log('🔘 Bouton Implémenter cliqué:', priority.title);
                          console.log('onImplement défini?', !!onImplement);
                          console.log('onImplement type:', typeof onImplement);
                          if (onImplement) {
                            console.log('🔘 Appel de onImplement...');
                            onImplement(priority);
                          } else {
                            console.error('❌ onImplement n\'est pas défini!');
                          }
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '8px 16px',
                          backgroundColor: getImpactColor(priority.impact),
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          width: '100%',
                          pointerEvents: 'auto',
                        }}
                      >
                        Implémenter
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Onglet Gains rapides */}
          {activeTab === 'quickwins' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {plan.quickWins.map((win, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '13px', color: '#1b5e20', border: '1px solid #4CAF50' }}>
                  ✓ {win}
                </div>
              ))}
            </div>
          )}

          {/* Onglet Structurel */}
          {activeTab === 'structural' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {plan.structuralImprovements.map((improvement, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: '#f3e5f5', borderRadius: '4px', fontSize: '13px', color: '#4a148c', border: '1px solid #9c27b0' }}>
                  • {improvement}
                </div>
              ))}
            </div>
          )}

          {/* Onglet Performance */}
          {activeTab === 'performance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {plan.performanceImprovements.map((improvement, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', fontSize: '13px', color: '#e65100', border: '1px solid #ff9800' }}>
                  ⚡ {improvement}
                </div>
              ))}
            </div>
          )}

          {/* Onglet Maintenance */}
          {activeTab === 'maintenance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {plan.maintenanceImprovements.map((improvement, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px', color: '#1565c0', border: '1px solid #2196f3' }}>
                  🔧 {improvement}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
